import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS } from "../../config/apiConfig";
import DrawerLoading from "../DrawerLoading";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import CustomCreditQuery from "../queries/CustomCreditQuery";
import CompletaPlusCNPJQuery from "../queries/CompletaPlusCNPJQuery";
import CompletaPlusCPFQuery from "../queries/CompletaPlusCPFQuery";
import PositivoPlusCNPJQuery from "../queries/PositivoPlusCNPJQuery";
import PositivoPlusCPFQuery from "../queries/PositivoPlusCPFQuery";
import ScrBacenScoreQuery from "../queries/ScrBacenScoreQuery";
import { motion } from "framer-motion";
import { FaSearch } from "react-icons/fa";
import { FiFileText } from "react-icons/fi";
import { Tooltip } from "react-tooltip";

const QueriesCreditTable = () => {
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);

  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axios.get(
        `${USERS.GET_USER_QUERY_PERMISSIONS}`
      );
      return items.data.filter(
        (query) => query.categoryId === 1 && !query.isModular
      );
    }
  );

  useEffect(() => {
    if (userQueryPermissions?.length && !selectedQuery) {
      setSelectedQuery(userQueryPermissions[0]);
      setSelectedPrice(userQueryPermissions[0]?.price || 0);
    }
  }, [userQueryPermissions, selectedQuery]);

  useEffect(() => {
    if (selectedQuery) {
      const match = userQueryPermissions.find((q) => q.id === selectedQuery.id);
      setSelectedPrice(match?.price || 0);
    }
  }, [selectedQuery, userQueryPermissions]);

  const filteredQueries = userQueryPermissions?.filter((query) =>
    query?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const queries = [
    {
      id: 1,
      component: (
        <CompletaPlusCNPJQuery
          query={selectedQuery}
          selectedPrice={selectedPrice}
        />
      ),
    },
    {
      id: 2,
      component: (
        <CompletaPlusCPFQuery
          query={selectedQuery}
          selectedPrice={selectedPrice}
        />
      ),
    },
    {
      id: 3,
      component: (
        <PositivoPlusCNPJQuery
          query={selectedQuery}
          selectedPrice={selectedPrice}
        />
      ),
    },
    {
      id: 4,
      component: (
        <PositivoPlusCPFQuery
          query={selectedQuery}
          selectedPrice={selectedPrice}
        />
      ),
    },
    {
      id: 5,
      component: (
        <ScrBacenScoreQuery
          query={selectedQuery}
          selectedPrice={selectedPrice}
        />
      ),
    },
  ];

  const QueryCard = ({ query, index }) => {
    const titleRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const el = titleRef.current;
      if (el) {
        requestAnimationFrame(() => {
          setIsTruncated(el.scrollWidth > el.clientWidth);
        });
      }
    }, [query.name]);

    return (
      <motion.div
        key={query.id}
        initial={{ opacity: 0, filter: "blur(6px)" }}
        animate={{ opacity: 1, filter: "blur(0px)" }}
        transition={{
          duration: 0.4,
          delay: index * 0.05,
          ease: "easeOut",
        }}
        className="relative flex flex-col justify-between p-4 bg-white rounded-lg shadow-md transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer border border-green-100"
        onClick={() => {
          setSelectedQuery(query);
          setSelectedPrice(query.price || 0);
          setIsListVisible(false);
        }}
      >
        <div className="flex items-center gap-2 border-b border-gray-200 pb-2 mb-2">
          <FiFileText className="text-green-500 shrink-0" size={16} />
          {isTruncated ? (
            <>
              <h3
                ref={titleRef}
                data-tooltip-id={`tooltip-title-${query.id}`}
                data-tooltip-content={query.name}
                className="font-semibold text-sm uppercase text-gray-800 truncate"
              >
                {query.name}
              </h3>
              <Tooltip
                id={`tooltip-title-${query.id}`}
                place="top"
                effect="solid"
                className="tooltip-wide text-sm text-left max-w-xs"
              />
            </>
          ) : (
            <h3
              ref={titleRef}
              className="font-semibold text-sm text-gray-800 truncate uppercase"
            >
              {query.name}
            </h3>
          )}
        </div>
        <span className="text-2xs text-gray-500 bg-gray-100 px-1 py-1 rounded self-start">
          Consulta de Crédito
        </span>
      </motion.div>
    );
  };

  if (isLoading) return <DrawerLoading />;
  if (!userQueryPermissions?.length) return <ForbiddenQueriesPermission />;

  return (
    <div className="w-full px-4 md:px-12 pt-4 min-h-screen">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-3">
          Consultas de Crédito 📊
        </h1>
        <p className="text-lg text-gray-500">
          Escolha uma das opções para visualizar as informações completas.
        </p>
      </div>

      {isListVisible ? (
        <>
          <div className="mb-8 relative">
            <input
              type="text"
              className="p-3 pl-10 w-full border border-gray-300 rounded-full text-lg focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-200"
              placeholder="Pesquisar consulta"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              size={18}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
            {filteredQueries?.map((query, index) => (
              <QueryCard query={query} index={index} />
            ))}
          </div>
        </>
      ) : (
        <div>
          <button
            onClick={() => setIsListVisible(true)}
            className="flex items-center text-sm text-green-700 hover:text-green-900 font-medium mb-4 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar para a listagem
          </button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {selectedQuery?.queryCustomizedId ? (
              <CustomCreditQuery
                query={selectedQuery}
                selectedPrice={selectedPrice}
              />
            ) : (
              queries.find((q) => q.id === selectedQuery?.id)?.component
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default QueriesCreditTable;
